/**
 * Sort array of items by order, then by name. 
 * The fields 'order' and 'name' are required.
 * @param {*} items 
 * @returns 
 */
export const sortOrderName = (items, field) => {
  // sort by order, then by name
  items = items.sort((a, b) => {
  const orderA = parseInt(a.order, 10)
  const orderB = parseInt(b.order, 10)

  // Sort by order
  if (!isNaN(orderA) && !isNaN(orderB)) {
    if (orderA !== orderB) {
      return orderA - orderB; // Sort by order in ascending order
    }
  } else if (!isNaN(orderA)) {
    return -1 // Move items with valid order value before items without order
  } else if (!isNaN(orderB)) {
    return 1 // Move items with valid order value before items without order
  }

  // Sort by name if order values are the same or neither has an order value
  const nameA = a[field].toLowerCase()
  const nameB = b[field].toLowerCase()

  if (nameA < nameB) {
    return -1 // Sort nameA before nameB
  }
  if (nameA > nameB) {
    return 1 // Sort nameA after nameB
  }
  
  return 0 // Names are equal
})

return items
}

/**
 * Sort array of items by a specified date field in descending order. 
 * If the item does not have the specified date field or the date is invalid, it is placed first.
 * @param {*} items 
 * @param {string} dateField - The date field in the objects to sort by.
 * @returns 
 */
export const sortByDate = (items, dateField) => {
  return items.sort((a, b) => {
      const dateA = new Date(a[dateField])
      const dateB = new Date(b[dateField])

      // Check if dates are valid
      const isValidDateA = !isNaN(dateA) && a[dateField] != null
      const isValidDateB = !isNaN(dateB) && b[dateField] != null

      // Sort by date in descending order
      if (isValidDateA && isValidDateB) {
          return dateB - dateA
      } else if (isValidDateA) {
          return 1 // Move items with valid date after items without date
      } else if (isValidDateB) {
          return -1 // Move items with valid date after items without date
      } else {
          // Both are invalid
          return 0
      }
  })
}

/**
 * Match outletId in article to outlet in outletList and save outlet to article.
 * @param {*} outlets 
 * @param {*} article 
 * @returns 
 */
export const attachOutletToArticle = (outlets, article) => {
  const outlet = outlets.find((item) => item.id === article.outletId)
  if (outlet !== undefined) {
    article.outlet = outlet
  }
  return article
}

/**
 * Articles will be grouped by outlet, and sorted by outlet.order. 
 * If outlet.order is null, it will be grouped under a new outlet named "Other Outlets".
 * Add outlet to each article based on outletId.
 * @param {*} articles 
 * @param {*} outlets 
 * @returns Returns a list of outlets that are in the articles.
 */
export const addOutletToArticles = (articles, outlets) => {
  let outletsInArticles = []
  for (let article of articles) {
    for (let outlet of outlets) {
      if (article.outletId === outlet.id) {
        article.outlet = outlet

        // Check if outlet.order is empty, if so, create and add "Other Outlets"
        if (outlet.order) {
          // Check if the outlet already exists in outletsInArticles before pushing
          if (!outletsInArticles.some(existingOutlet => existingOutlet.id === outlet.id)) {
            outletsInArticles.push(outlet)
          }
        } else {
          const otherOutlets = {
            id: 'other-outlets',
            name: 'Other Outlets',
            originalId: article.outlet.id,
            originalName: article.outlet.name,
            order: null
          }

          // Assign the new outlet to the article
          article.outlet = otherOutlets

          // Check if "Other Outlets" already exists in outletsInArticles before pushing
          if (!outletsInArticles.some(existingOutlet => existingOutlet.id === otherOutlets.id)) {
            outletsInArticles.push(otherOutlets)
          }
        }
        
        break
      }
    }
  }
  return outletsInArticles;
}


/**
 * Given a list of articles and outlets, match the article outletName to the 
 * outlet object name.
 * If matched, replace the article.outlet with the outlet object.
 * If not matched, replace the article.outlet with new 'Other Outlets' outlet object.
 * 
 * Also, return the unique outlet objects within the list of articles.
 * 
 * @param {*} articles List of articles.
 * @param {*} outlets List of outlets.
 * @returns List of unique outlet objects and updated list of articles.
 */
export const getUniqueOutletObjects = (articles, outlets) => {
  const outletNames = new Set()

  articles.forEach(article => {
    if (article.outletName) {
      outletNames.add(article.outletName)
    }
  })

  // Replace outlet names with outlet objects in the articles
  articles = articles.map(article => {
    if (article.outletName) {
      const matchingOutlet = outlets.find(outlet => outlet.name === article.outletName)
      if (matchingOutlet) {
        // If matching outlet, replace article.outlet object
        article.outlet = matchingOutlet
      } else {
        // If no matching outlet, add to "Other Outlets"
        article.outlet = {
          id: 'other-outlets',
          name: 'Other Outlets',
          originalName: article.outletName,
          order: null
        }
      }
    }
    return article;
  })

  // Collect all the unique names that are not found in the outlets array
  const otherOutletNames = [...new Set(outletNames)].filter(name => 
    !outlets.some(outlet => outlet.name === name)
  );

  // Map all the unique names that are found in the outlets array
  let uniqueOutletObjects = [...new Set(outletNames)]
    .filter(name => outlets.some(outlet => outlet.name === name))
    .map(name => outlets.find(outlet => outlet.name === name))

  // If there are any "Other Outlets" names, add a single "Other Outlets" object
  if (otherOutletNames.length > 0) {
    uniqueOutletObjects.push({
      id: 'other-outlets',
      name: 'Other Outlets',
      originalNames: otherOutletNames,
      order: null
    })
  }

  uniqueOutletObjects = sortOutletByOrder(uniqueOutletObjects)

  return [uniqueOutletObjects, articles]
}


export const sortOutletByOrder = (outlets) => {
  outlets.sort((a, b) => {
    // Treat null as the highest order
    const orderA = a.order === null ? Number.MAX_SAFE_INTEGER : a.order
    const orderB = b.order === null ? Number.MAX_SAFE_INTEGER : b.order
    return orderA - orderB
  })
  return outlets
}